import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _find from 'lodash/find';
import { AjaxContext, AppContext, UserContext } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import {
	VIEW_FIELD_KEYS,
	DEFAULT_PORTFOLIO_DATE_RANGE,
	GET_USERS_HEARTBEAT_DETAILS_BULK_ENDPOINT,
	GET_USER_CALLING_SLOTS_ENDPOINT
} from './constants';

const useService = () => {
	const {
		app: { appName }
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);

	const getUserHeartBeatStatus = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getUsersHeartBeatDetailsBulk });
		const result = await postData({
			endpoint: `${GET_USERS_HEARTBEAT_DETAILS_BULK_ENDPOINT}/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.userStats}-getUsersHeartBeatDetailsBulk`,
			queryParams: {
				appName
			},
			body: { zuids: [uid] },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = _get(result, ['data', 'data'], {});
		const heartBeatData = data[uid];

		const getLastSeenText = (lastTimeStamp: number) => {
			if (!lastTimeStamp) return 'More than 2 mins ago';

			const now = Date.now();
			const diffMs = now - lastTimeStamp;
			const diffSeconds = Math.floor(diffMs / 1000);
			const diffMinutes = Math.floor(diffMs / (1000 * 60));
			const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
			const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

			if (diffSeconds < 10) return 'Just Now';
			if (diffSeconds < 60) return `${diffSeconds} second(s) ago`;
			if (diffMinutes < 60) return `${diffMinutes} minute(s) ago`;
			if (diffHours < 24) return `${diffHours} hour(s) ago`;
			return `${diffDays} days ago`;
		};

		if (heartBeatData) {
			const isOnline = _get(heartBeatData, 'status', false);
			const lastTimeStamp = _get(heartBeatData, 'lastTimeStamp');

			return {
				[VIEW_FIELD_KEYS.ONLINE_STATUS]: isOnline ? '🟢 Online' : '🔴 Offline',
				[VIEW_FIELD_KEYS.LAST_SEEN]: isOnline ? 'Currently active' : getLastSeenText(lastTimeStamp)
			};
		}

		return {
			[VIEW_FIELD_KEYS.ONLINE_STATUS]: '🔴 Offline',
			[VIEW_FIELD_KEYS.LAST_SEEN]: 'No data available'
		};
	}, [uid, permissionsConfig, appName, postData]);

	const getUserActivity = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getUserSignUpAndLastActivityV2 });
		const result = await postData({
			endpoint: `/dashboard/getUserSignUpAndLastActivityV2/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.userStats}-getUserSignUpAndLastActivityV2`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const records = _get(result, ['data'], []);
		const data = records.length > 0 ? records[0] : null;

		return {
			[VIEW_FIELD_KEYS.SIGN_UP]: {
				[VIEW_FIELD_KEYS.TIMESTAMP]: _get(data, VIEW_FIELD_KEYS.TIMESTAMP),
				[VIEW_FIELD_KEYS.LOCATION]: _get(data, VIEW_FIELD_KEYS.LOCATION),
				[VIEW_FIELD_KEYS.IP_ADDRESS]: _get(data, VIEW_FIELD_KEYS.IP_ADDRESS)
			},
			[VIEW_FIELD_KEYS.LAST_ACTIVITY]: {
				[VIEW_FIELD_KEYS.LA_IP_ADDRESS]: _get(data, VIEW_FIELD_KEYS.LA_IP_ADDRESS),
				[VIEW_FIELD_KEYS.LA_LOCATION]: _get(data, VIEW_FIELD_KEYS.LA_LOCATION),
				[VIEW_FIELD_KEYS.LA_LOGIN_TIMESTAMP]: _get(data, VIEW_FIELD_KEYS.LA_LOGIN_TIMESTAMP)
			}
		};
	}, []);

	const getUserPortfolio = useCallback(async (filters: any) => {
		const fromToDate = _map(_get(filters, 'fromToDate', DEFAULT_PORTFOLIO_DATE_RANGE), (val) => val.getTime());
		const permission = _find(permissionsConfig, { actionName: ACTION.getUserPortfolio });
		const result = await postData({
			endpoint: `/dashboard/getUserPortfolio/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.userStats}-getUserPortfolio`,
			queryParams: {
				appName
			},
			body: { uid, fromToDate },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = _get(result, ['data', 'data'], {});

		return data;
	}, []);

	const getUserNetPortfolio = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getUserNetPortfolio });
		const result = await postData({
			endpoint: `/dashboard/getUserNetPortfolio/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.userStats}-getUserNetPortfolio`,
			queryParams: {
				appName
			},
			body: { zupeeUserId: uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = _get(result, ['data', 'data'], {});
		return data;
	}, []);

	const getUserCallingSlots = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getUserCallingSlots });
		const result = await postData({
			endpoint: `${GET_USER_CALLING_SLOTS_ENDPOINT}/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.userStats}-getUserCallingSlots`,
			queryParams: {
				appName
			},
			body: { zuid: uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = _get(result, ['data', 'data'], []);
		const callingSlotData = data.length > 0 ? data[0] : null;

		if (callingSlotData) {
			return {
				[VIEW_FIELD_KEYS.RECOMMENDED_CALLING_SLOTS]: _get(callingSlotData, VIEW_FIELD_KEYS.RECOMMENDED_CALLING_SLOTS, 'N/A'),
				[VIEW_FIELD_KEYS.BEST_SLOT]: _get(callingSlotData, VIEW_FIELD_KEYS.BEST_SLOT, 'N/A'),
				[VIEW_FIELD_KEYS.SECOND_BEST_SLOT]: _get(callingSlotData, VIEW_FIELD_KEYS.SECOND_BEST_SLOT, 'N/A'),
				[VIEW_FIELD_KEYS.THIRD_BEST_SLOT]: _get(callingSlotData, VIEW_FIELD_KEYS.THIRD_BEST_SLOT, 'N/A'),
				[VIEW_FIELD_KEYS.BEST_SLOT_ALL_HOURS]: _get(callingSlotData, VIEW_FIELD_KEYS.BEST_SLOT_ALL_HOURS, 'N/A'),
				[VIEW_FIELD_KEYS.SECOND_BEST_SLOT_ALL_HOURS]: _get(
					callingSlotData,
					VIEW_FIELD_KEYS.SECOND_BEST_SLOT_ALL_HOURS,
					'N/A'
				),
				[VIEW_FIELD_KEYS.THIRD_BEST_SLOT_ALL_HOURS]: _get(callingSlotData, VIEW_FIELD_KEYS.THIRD_BEST_SLOT_ALL_HOURS, 'N/A')
			};
		}

		return {
			[VIEW_FIELD_KEYS.RECOMMENDED_CALLING_SLOTS]: 'No data available',
			[VIEW_FIELD_KEYS.BEST_SLOT]: 'No data available',
			[VIEW_FIELD_KEYS.SECOND_BEST_SLOT]: 'No data available',
			[VIEW_FIELD_KEYS.THIRD_BEST_SLOT]: 'No data available',
			[VIEW_FIELD_KEYS.BEST_SLOT_ALL_HOURS]: 'No data available',
			[VIEW_FIELD_KEYS.SECOND_BEST_SLOT_ALL_HOURS]: 'No data available',
			[VIEW_FIELD_KEYS.THIRD_BEST_SLOT_ALL_HOURS]: 'No data available'
		};
	}, [uid, permissionsConfig, appName, postData]);

	return {
		getUserActivity,
		getUserPortfolio,
		getUserNetPortfolio,
		getUserHeartBeatStatus,
		getUserCallingSlots
	};
};

export default useService;
